/**
 * Base mixin for chart templates
 * Provides shared functionality for chart rendering, pagination, filtering, and hover states
 */
export default {
    computed: {
        effectiveSectionId() {
            return this.section_id != null ? this.section_id : this.id;
        }
    },
    data() {
        return {
            chart: null,
            num_of_datasets: 0,
            render_timeout: null,
            if_chart_has_data: false,
            if_chart_filter_applied: false,
            chart_filter_label: [],
            chart_filter_label_index: [null, null],
            font_size: 12,
            max_value: 0,
            hoveredIndex: null,
            hoverOpacity: 0,
            hoverAnimationFrame: null,
            hoverTimeout: null,
            hideTimeout: null,
            pendingHoverIndex: null,
            // Pagination and label visibility tracking
            currentPageStart: 0,
            displayedDataCount: 0,
            totalDataLength: 0,
            isDataTruncated: false,
            pageInitialized: false,
        };
    },
    watch: {
        chart_selected: {
            handler(newVal) {
                if (newVal?.data) {
                    this.pageInitialized = false;
                    this.debouncedRender();
                }
            },
            deep: true
        },
        if_refreshing: {
            handler() {
                this.debouncedRender();
            }
        },
        applied_chart_filter: {
            handler(newVal) {
                this.if_chart_filter_applied = newVal?.if_chart_filter_applied && this.id === newVal?.section_chart_id;
                // Sync chart_filter_label from applied_chart_filter when filter is applied to this chart
                if (this.if_chart_filter_applied && newVal?.chart_filters && newVal.chart_filters.length > 0) {
                    this.chart_filter_label = newVal.chart_filters.map(filter => filter.value);
                } else if (!this.if_chart_filter_applied) {
                    this.chart_filter_label = [];
                }
                this.pageInitialized = false;
                this.debouncedRender();
            },
            deep: true
        },
        'chart_selected.size': {
            handler(newSize, oldSize) {
                // When chart size changes, reset pagination to first page
                if (newSize !== oldSize) {
                    this.currentPageStart = 0;
                    this.pageInitialized = false;
                    this.debouncedRender();
                }
            }
        },
        forceShowAllData: {
            handler() {
                this.currentPageStart = 0;
                this.pageInitialized = false;
                this.debouncedRender();
            }
        },
        size: {
            handler(newSize, oldSize) {
                if (newSize !== oldSize) {
                    this.currentPageStart = 0;
                    this.pageInitialized = false;
                    this.debouncedRender();
                }
            }
        },
        if_show_label: {
            handler() {
                // For toggle visibility: use chart.update() to preserve dataset visibility (legend-hidden state)
                // A full renderChart() would destroy and recreate the chart, resetting hidden datasets
                this.$nextTick(() => {
                    if (this.chart) {
                        this.chart.update('none');
                    } else {
                        this.renderChart();
                    }
                });
            }
        }
    },
    mounted() {
        const export_report_class = document.querySelector('.export-report');
        if (export_report_class) {
            // if on export view, make font bigger, set to 15 for now
            this.font_size = 15;
        }
        
        if (this.chart_selected?.data) {
            this.debouncedRender();
        }
    },
    beforeUnmount() {
        if (this.render_timeout) {
            clearTimeout(this.render_timeout);
            this.render_timeout = null;
        }
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
        }
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        this.destroyChart();
    },
    methods: {
        /**
         * Destroy chart instance and reset state
         */
        destroyChart() {
            if (this.chart) {
                try {
                    this.chart.destroy();
                } catch (error) {
                    // Ignore errors during cleanup
                }
                this.chart = null;
            }
            // Reset hovered index and opacity
            this.hoveredIndex = null;
            this.hoverOpacity = 0;
            // Cancel any pending animation frames
            if (this.hoverAnimationFrame) {
                cancelAnimationFrame(this.hoverAnimationFrame);
                this.hoverAnimationFrame = null;
            }
            // Tooltip cleanup is handled by chart_tooltip mixin
            if (typeof this.cleanupChartTooltip === 'function') {
                this.cleanupChartTooltip();
            }
        },

        /**
         * Debounced render method - calls renderChart after 500ms delay
         * Override this method in components that need custom initialization logic
         */
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    // Default initialization - components can override this
                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.renderChart();
                    this.if_chart_has_data = true;
                    // Re-emit pagination state now that chart has data
                    if (typeof this.emitPaginationState === 'function') {
                        this.emitPaginationState();
                    }
                });
            }, 500); // 500ms debounce
        },

        /**
         * Animate hover opacity for smooth fade-in/fade-out effects
         * @param {number} targetOpacity - Target opacity (0-1)
         * @param {Object} chart - Chart.js instance
         */
        animateHoverOpacity(targetOpacity, chart) {
            if (this.hoverAnimationFrame) {
                cancelAnimationFrame(this.hoverAnimationFrame);
            }
            
            const startOpacity = this.hoverOpacity;
            const duration = 200; // 200ms smooth fade-in
            const startTime = performance.now();
            
            const animate = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Ease-out function for smooth animation
                const easeOut = 1 - Math.pow(1 - progress, 2);
                this.hoverOpacity = startOpacity + (targetOpacity - startOpacity) * easeOut;
                
                // Check if chart instance is still valid and has a context before updating
                if (chart && chart.ctx) {
                    chart.update('none'); // Update without animation
                }
                
                if (progress < 1) {
                    this.hoverAnimationFrame = requestAnimationFrame(animate);
                } else {
                    this.hoverOpacity = targetOpacity;
                    this.hoverAnimationFrame = null;
                }
            };
            
            this.hoverAnimationFrame = requestAnimationFrame(animate);
        },

        /**
         * Get pagination text for display (e.g., "Page 1 of 3")
         * @returns {string} Pagination text
         */
        getPaginationText() {
            // Safety check: avoid division by zero
            if (!this.displayedDataCount || !this.totalDataLength) {
                return '';
            }
            
            // Calculate current page (1-indexed)
            const currentPage = Math.floor(this.currentPageStart / this.displayedDataCount) + 1;
            
            // Calculate total pages
            const totalPages = Math.ceil(this.totalDataLength / this.displayedDataCount);
            
            return `Page ${currentPage} of ${totalPages}`;
        },

        /**
         * Emit current pagination state to parent
         */
        emitPaginationState() {
            this.$emit('update:pagination', {
                isTruncated: this.isDataTruncated && this.if_chart_has_data,
                currentPageStart: this.currentPageStart,
                displayedDataCount: this.displayedDataCount,
                totalDataLength: this.totalDataLength,
                paginationText: this.getPaginationText()
            });
        },

        /**
         * Navigate to previous page of data
         * Uses page boundaries so that from the last page we go to the true previous page (e.g. page 2 of 3), not an offset that shows "page 1 of 3"
         */
        previousPage() {
            const pageSize = this.calculateLabelsToShow();
            if (!pageSize || !this.totalDataLength) {
                this.renderChart();
                return;
            }
            const totalPages = Math.ceil(this.totalDataLength / pageSize);
            const isOnLastPage = this.currentPageStart + pageSize >= this.totalDataLength;
            // When on last page we're at page index (totalPages - 1); otherwise use floor(currentPageStart / pageSize)
            const currentPageIndex = isOnLastPage
                ? totalPages - 1
                : Math.floor(this.currentPageStart / pageSize);
            const previousPageStart = Math.max(0, (currentPageIndex - 1) * pageSize);
            this.currentPageStart = previousPageStart;
            this.renderChart();
        },

        /**
         * Navigate to next page of data
         */
        nextPage() {
            const pageSize = this.calculateLabelsToShow();
            const maxStart = Math.max(0, this.totalDataLength - pageSize);
            this.currentPageStart = Math.min(maxStart, this.currentPageStart + pageSize);
            this.renderChart();
        },

        /**
         * Detects if chart uses time-based data by checking group_by value
         * @returns {boolean} True if chart is time-based
         */
        isTimeBased() {
            const groupBy = String(this.chart_selected?.group_by?.[0]?.text || '').toLowerCase();
            return groupBy.includes('year') || 
                   groupBy.includes('month') || 
                   groupBy.includes('date') ||
                   groupBy.includes('day');
        },

        /**
         * Parse labels to find indices of the most recent year/month/date
         * Returns array of indices that belong to the most recent period
         * @returns {number[]} Array of indices
         */
        getMostRecentPeriodIndices() {
            if (!this.isTimeBased()) return [];
            
            const labels = this.chart_selected.labels || [];
            if (labels.length === 0) return [];
            
            // Extract year from labels - could be "2023 (Nov-Oct)" or just "2023"
            const yearPattern = /(\d{4})/;
            const years = labels.map(label => {
                const match = String(label).match(yearPattern);
                return match ? parseInt(match[1]) : null;
            }).filter(y => y !== null);
            
            if (years.length === 0) return [];
            
            // Find the most recent (largest) year
            const mostRecentYear = Math.max(...years);
            
            // Return indices of labels that contain this year
            return labels.map((label, index) => {
                const match = String(label).match(yearPattern);
                return match && parseInt(match[1]) === mostRecentYear ? index : -1;
            }).filter(i => i !== -1);
        },

        /**
         * Determine if data labels should be shown based on toggle state and chart properties
         * @returns {boolean} True if labels should be shown
         */
        shouldShowDataLabels() {
            // When toggle is explicitly ON, always show labels
            if (this.if_show_label === true) {
                return true;
            }
            
            // When toggle is explicitly OFF, never show labels (user has toggled off)
            if (this.if_show_label === false) {
                return false;
            }
            
            // If if_show_label is undefined/null (shouldn't happen with current implementation,
            // but keeping for safety), fall back to default behavior
            const chartSize = this.size || this.chart_selected?.size || 'small';
            
            // Small/Medium charts: never show labels when toggle is not set
            if (chartSize === 'small' || chartSize === 'medium') {
                return false;
            }
            
            // Large and X-Large charts
            if (chartSize === 'large' || chartSize === 'x-large') {
                // X-Large with fewer than 15 datapoints: show labels
                const totalLabels = this.chart_selected?.labels?.length || 0;
                const numDatasets = this.num_of_datasets || (this.chart_selected?.data ? Object.keys(this.chart_selected.data).length : 0);
                const totalDataPoints = totalLabels * numDatasets;
                if (chartSize === 'x-large' && totalDataPoints < 15) {
                    return true;
                }
                // Time-based charts: show labels (display function will handle > 15 case)
                if (this.isTimeBased()) {
                    return true;
                }
                // Non-time-based: never show labels
                return false;
            }
            
            return false;
        },

        /**
         * Get truncated data based on current page
         * Returns { labels, datasets } with sliced labels and data points
         * @param {Object} fullData - Full chart data with labels and datasets
         * @returns {Object} Truncated data
         */
        getTruncatedData(fullData) {
            // If not truncated, return full data
            if (!this.isDataTruncated) {
                this.currentPageStart = 0;
                return fullData;
            }
            
            const totalLabels = fullData.labels?.length || 0;
            const labelsToShow = this.displayedDataCount;
            
            // Ensure currentPageStart is within bounds
            const maxStart = Math.max(0, totalLabels - labelsToShow);
            this.currentPageStart = Math.min(this.currentPageStart, maxStart);
            
            const endIndex = Math.min(this.currentPageStart + labelsToShow, totalLabels);
            
            // Slice labels
            const truncatedLabels = fullData.labels.slice(this.currentPageStart, endIndex);
            
            // Slice data within each dataset
            const truncatedDatasets = fullData.datasets.map(dataset => {
                const newDataset = { ...dataset };
                
                if (Array.isArray(dataset.data)) {
                    newDataset.data = dataset.data.slice(this.currentPageStart, endIndex);
                }
                
                // If backgroundColor is an array, slice it too
                if (Array.isArray(dataset.backgroundColor)) {
                    newDataset.backgroundColor = dataset.backgroundColor.slice(this.currentPageStart, endIndex);
                }
                
                return newDataset;
            });
            
            return {
                labels: truncatedLabels,
                datasets: truncatedDatasets
            };
        },

        /**
         * Get color array for a given color name
         * Colors are from dark to light shades (A-F for most colors, A-G for gray)
         * @param {string} color - Color name (yellow, green, blue, purple, gray)
         * @returns {string[]} Array of RGB color values
         */
        getColor(color) {
            const colors = {
                yellows: [
                    'rgb(250 174 0)',    // A
                    'rgb(251 189 44)',   // B
                    'rgb(252 201 89)',   // C
                    'rgb(253 215 133)',  // D
                    'rgb(254 228 178)',  // E
                    'rgb(255 242 222)',  // F
                ],
                greens: [
                    'rgb(152 203 46)',   // A
                    'rgb(169 213 80)',   // B
                    'rgb(185 224 110)',  // C
                    'rgb(202 234 144)',  // D
                    'rgb(218 245 176)',  // E
                    'rgb(235 255 208) ', // F
                ],
                blues: [
                    'rgb(80 66 255)',    // A
                    'rgb(104 113 255)',  // B
                    'rgb(128 142 254)',  // C
                    'rgb(159 173 255)',  // D
                    'rgb(195 205 255)',  // E
                    'rgb(232 236 255)',  // F
                ],
                purples: [
                    'rgb(255 25 81)',    // A
                    'rgb(255 66 112)',   // B
                    'rgb(255 110 143)',  // C
                    'rgb(255 152 175)',  // D
                    'rgb(255 195 206)',  // E
                    'rgb(255 237 237)',  // F
                ],
                grays: [
                    'rgb(78, 79, 79)',   // A
                    'rgb(107, 108, 108)', // B
                    'rgb(136, 137, 137)', // C
                    'rgb(165, 166, 166)', // D
                    'rgb(195, 196, 196)', // E
                    'rgb(212, 212, 212)', // F
                    'rgb(228, 228, 228)'  // G
                ],
            };
            if (color == 'yellow') {
                return colors.yellows;
            }
            if (color == 'green') {
                return colors.greens;
            }
            if (color == 'blue') {
                return colors.blues;
            }
            if (color == 'purple') {
                return colors.purples;
            }
            if (color == 'gray') {
                return colors.grays;
            }
            return colors.yellows;
        },

        /**
         * Returns array indices for colors based on number of datasets
         * Pattern: 1=A, 2=A+C, 3=A+C+E, 4=A+C+D+F, 5=A+B+C+D+E, 6=A+B+C+D+E+F
         * @param {number} numDatasets - Number of datasets
         * @returns {number[]} Array of color indices
         */
        getColorIndicesForDatasets(numDatasets) {
            const patterns = {
                1: [0],                    // A
                2: [0, 2],                 // A, C
                3: [0, 2, 4],              // A, C, E
                4: [0, 2, 3, 5],           // A, C, D, F
                5: [0, 1, 2, 3, 4],        // A, B, C, D, E
                6: [0, 1, 2, 3, 4, 5],     // A, B, C, D, E, F
            };
            return patterns[Math.min(numDatasets, 6)] || patterns[1];
        },

        /**
         * Gets a color by letter (A-F for most colors, A-G for gray)
         * @param {string} color - Color name (yellow, green, blue, purple, gray)
         * @param {string} letter - Letter from A to F (or G for gray)
         * @returns {string} RGB color value
         */
        getColorByLetter(color, letter) {
            // Handle undefined or null letter
            if (!letter || typeof letter !== 'string') {
                const colorArray = this.getColor(color);
                return colorArray[0];
            }
            const letterToIndex = {
                'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6
            };
            const index = letterToIndex[letter.toUpperCase()] || 0;
            const colorArray = this.getColor(color);
            return colorArray[index] || colorArray[0];
        },

        /**
         * Calculates a nice rounded-up maximum value for the y-axis
         * Logic: if (top_axis - highest_point) < 35% of the space between axis ticks, add additional top axis
         * @param {number} maxValue - Maximum value in the data
         * @returns {number} Calculated y-axis maximum
         */
        getYAxisMax(maxValue) {
            if (maxValue <= 0) {
                return 1;
            }
            
            // Calculate the order of magnitude
            const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
            const normalized = maxValue / magnitude;
            
            // Calculate natural top axis (round up to next nice number)
            let naturalTop;
            if (normalized <= 1) {
                naturalTop = 1 * magnitude;
            } else if (normalized <= 1.5) {
                naturalTop = 1.5 * magnitude;
            } else if (normalized <= 2) {
                naturalTop = 2 * magnitude;
            } else if (normalized <= 2.5) {
                naturalTop = 2.5 * magnitude;
            } else if (normalized <= 4) {
                naturalTop = 4 * magnitude;
            } else if (normalized <= 5) {
                naturalTop = 5 * magnitude;
            } else if (normalized <= 10) {
                naturalTop = 10 * magnitude;
            } else {
                naturalTop = magnitude * 10;
            }
            
            // Calculate gap between natural top and highest point
            const gap = naturalTop - maxValue;
            
            // Calculate 35% of the space between axis ticks
            // For maxTicksLimit: 5, the interval is approximately naturalTop / 4
            const interval = naturalTop / 4;
            const threshold = interval * 0.35;
            
            // If gap is less than 35% of interval, add additional top axis
            if (gap < threshold) {
                // Get next level up
                const naturalTopNormalized = naturalTop / magnitude;
                if (naturalTopNormalized === 1) {
                    return 1.5 * magnitude;
                } else if (naturalTopNormalized === 1.5) {
                    return 2 * magnitude;
                } else if (naturalTopNormalized === 2) {
                    return 2.5 * magnitude;
                } else if (naturalTopNormalized === 2.5) {
                    return 4 * magnitude;
                } else if (naturalTopNormalized === 4) {
                    return 5 * magnitude;
                } else if (naturalTopNormalized === 5) {
                    return 10 * magnitude;
                } else {
                    return naturalTop * 2;
                }
            } else {
                // Gap is sufficient, use natural top axis
                return naturalTop;
            }
        },

        /**
         * Darkens a color by a percentage (0-1)
         * Handles both formats: rgb(r g b) and rgb(r, g, b)
         * @param {string} color - RGB color string like 'rgb(255 255 255)' or 'rgb(255, 255, 255)'
         * @param {number} percent - Percentage to darken (0.2 = 20%)
         * @returns {string} Darkened RGB color string
         */
        darkenColor(color, percent) {
            // Handle both formats: rgb(r g b) and rgb(r, g, b)
            const rgbMatch = color.match(/rgb\((\d+)[,\s]+(\d+)[,\s]+(\d+)\)/);
            if (!rgbMatch) return color;
            
            const r = Math.max(0, Math.floor(parseInt(rgbMatch[1]) * (1 - percent)));
            const g = Math.max(0, Math.floor(parseInt(rgbMatch[2]) * (1 - percent)));
            const b = Math.max(0, Math.floor(parseInt(rgbMatch[3]) * (1 - percent)));
            
            // Return in the same format as input (preserve comma vs space)
            const hasCommas = color.includes(',');
            return hasCommas ? `rgb(${r}, ${g}, ${b})` : `rgb(${r} ${g} ${b})`;
        },

        /**
         * Calculates bar thickness based on chart size and data length
         * @param {number} dataLength - Number of data points
         * @param {string} [chartSize] - Optional chart size override (defaults to this.size || this.chart_selected?.size || 'small')
         * @returns {number} Bar thickness in pixels
         */
        getBarThickness(dataLength, chartSize = null) {
            const size = chartSize || this.size || this.chart_selected?.size || 'small';
            
            // Module: always 15px
            if (size === 'module') {
                return 15;
            }
            
            // Large and X-large: 50px if datalength < 10, else 30px if <= 15, else 15px
            if (size === 'medium' || size === 'large' || size === 'x-large') {
                if (dataLength <= 10) {
                    return 50;
                } else if (dataLength > 10 && dataLength <= 15) {
                    return 30;
                } else {
                    return 15;
                }
            }
            
            // Small: 30px if datalength < 5, else 15px
            if (dataLength <= 5) {
                return 30;
            } else {
                return 15;
            }
        },

        /**
         * Format a value based on format type
         * This is the core formatting logic used by datalabel formatters
         * @param {number} value - The value to format
         * @param {string|string[]} formatType - Format type ('money', 'number', 'percent') or array for combo charts
         * @param {number} datasetIndex - Dataset index (for combo charts to select format type)
         * @param {boolean} [showSeriesPercentage] - Whether to show as percentage
         * @returns {string} Formatted value string
         */
        formatDataLabelValue(value, formatType, datasetIndex = 0, showSeriesPercentage = false) {
            // Handle combo charts where format_type is an array
            const actualFormatType = Array.isArray(formatType) 
                ? (datasetIndex === 0 ? formatType[0] : formatType[1])
                : formatType;

            // Handle percentage override
            if (showSeriesPercentage) {
                return this.percentage(value);
            }

            // Check if compressed numbers should be used (default to true for backward compatibility)
            const useCompressed = this.show_compressed_numbers !== false;

            // Format based on type
            if (actualFormatType === 'money') {
                if (useCompressed && value >= 1000) {
                    return this.currency_abbr(value);
                }
                if (this.max_value >= 10) {
                    return this.currency_with_zero(value);
                }
                return this.currency_with_decimal(value);
            }

            if (actualFormatType === 'number') {
                if (useCompressed && value >= 1000) {
                    return this.num_abbr(value);
                }
                if (this.max_value >= 10) {
                    return this.number_with_zero(value);
                }
                return this.number_with_decimal(value);
            }

            if (actualFormatType === 'percent') {
                return this.percentage(value);
            }

            return value;
        },

        /**
         * Base renderChart method - handles common chart rendering logic
         * Components can override this or call it and add chart-specific logic
         * @param {Function} [beforeRender] - Optional callback before chart creation (e.g., processValueIntoPrecentage)
         * @param {Function} [setupMouseLeave] - Optional callback to setup mouseleave handler
         */
        renderChart() {
            if (!this.chart_selected?.data) return;

            // Set pagination and dataset state early for accurate calculations
            this.num_of_datasets = Object.keys(this.chart_selected.data).length;
            this.totalDataLength = this.chart_selected.labels?.length || 0;
            this.displayedDataCount = this.forceShowAllData ? this.totalDataLength : this.calculateLabelsToShow();
            this.isDataTruncated = this.displayedDataCount < this.totalDataLength;

            // Emit state to parent for centralized pagination controls
            this.emitPaginationState();

            // Get full dataset first (will use displayedDataCount for styling)
            const fullDataset = this.getChartData();
            
            // Apply truncation
            const dataset = this.getTruncatedData(fullDataset);
            
            const options = this.getChartOptions(dataset);

            // First destroy any existing chart
            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (!ctx?.getContext) {
                    console.warn('Chart canvas not found or context not available');
                    return;
                }

                // Use helper to wait until the canvas is visible before creating the chart
                this.createChartWhenVisible(ctx, options, (chartInstance) => {
                    this.chart = markRaw(chartInstance);
                    
                    // Setup mouseleave handler - components can override this
                    this.setupChartMouseLeave(chartInstance);
                });
            });
        },

        /**
         * Reset all hover state and cancel any pending debounce timers.
         * Call this when the chart's section becomes inactive so stale highlights
         * do not reappear when the user switches back to the section.
         */
        resetHoverState() {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }
            if (this.hideTimeout) {
                clearTimeout(this.hideTimeout);
                this.hideTimeout = null;
            }
            this.pendingHoverIndex = null;
            if (this.hoveredIndex !== null) {
                this.hoveredIndex = null;
                this.hoverOpacity = 0;
                if (this.chart) {
                    this.chart.update('none');
                }
            }
            // Hide tooltip DOM element if it exists
            if (typeof this.getTooltipId === 'function') {
                const tooltipEl = document.getElementById(this.getTooltipId());
                if (tooltipEl) {
                    tooltipEl.style.opacity = 0;
                    tooltipEl.style.pointerEvents = 'none';
                }
            }
        },

        /**
         * Setup mouseleave event listener for chart canvas
         * Components can override this for custom mouseleave behavior
         * @param {Object} chartInstance - Chart.js instance
         */
        setupChartMouseLeave(chartInstance) {
            const canvas = chartInstance.canvas;
            if (!canvas) return;

            canvas.addEventListener('mouseleave', () => {
                // Clear any pending show timeout when leaving
                if (this.hoverTimeout) {
                    clearTimeout(this.hoverTimeout);
                    this.hoverTimeout = null;
                    this.pendingHoverIndex = null;
                }
                
                // Debounce hiding the hover state (500ms delay)
                // This matches the show debounce timing for consistency
                if (this.hoveredIndex !== null && !this.hideTimeout) {
                    this.hideTimeout = setTimeout(() => {
                        // Only hide if still not hovering and tooltip is also not being hovered
                        if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                            this.hoveredIndex = null;
                            this.animateHoverOpacity(0, chartInstance);

                            // Explicitly hide tooltip element to stay in sync
                            const tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                            if (tooltipEl) {
                                tooltipEl.style.opacity = 0;
                                tooltipEl.style.pointerEvents = 'none';
                            }
                        }
                        this.hideTimeout = null;
                    }, 500);
                }
            });
        }
    }
};
